import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { captureException } from "~/lib/analytics";
import { logger } from "~/lib/logger";

interface FeatureFlags {
    enableCommitments?: boolean;
    // AIDEV-NOTE: Feature flag to control part suggestions visibility/calculation
    enablePartSuggestions?: boolean;
}

export const useAccountFeatureFlags = () => {
    const apiClient = useApiClient();

    return useQuery<FeatureFlags, Error>({
        queryKey: ["account-feature-flags"],
        queryFn: async () => {
            try {
                const response = await apiClient.get("/account/feature-flags");

                if (!response.ok) {
                    if (response.status !== 401) {
                        const error = new Error(
                            `Failed to fetch feature flags: ${response.statusText}`,
                        );
                        logger
                            .withError(error)
                            .error("Failed to fetch account feature flags");
                        throw error;
                    }

                    return {};
                }

                const data = await response.json();
                return data.featureFlags || {};
            } catch (error) {
                logger
                    .withError(error as Error)
                    .error("Error in account feature flags query");
                captureException("Error fetching account feature flags", error);
                throw error;
            }
        },
    });
};

export const useUpdateAccountFeatureFlags = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (featureFlags: FeatureFlags) => {
            const response = await apiClient.put(
                "/account/feature-flags",
                featureFlags as Record<string, unknown>,
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    error.error || "Failed to update feature flags",
                );
            }

            const result = await response.json();
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["account-feature-flags"],
            });
        },
        onError: (error) => {
            logger
                .withError(error as Error)
                .error("Failed to update feature flags");
            captureException("Error updating account feature flags", error);
        },
    });
};
